import { ICart, PaymentMethod } from "../../models";

/*
 * Checkout form checks, shared by the order button (Cart), the field markers (CartOrder) and the
 * payment section (PaymentMethod). The order button stays enabled: a click on it with something
 * missing marks the fields and scrolls to the first one (focusOrderField).
 */

/** Form fields in the order they appear on the page; the first invalid one gets the focus. */
export const ORDER_FIELDS = [
  "name",
  "address",
  "zip",
  "place",
  "phone",
  "email",
  "paymentMethod",
  "terms",
] as const;

export type OrderField = typeof ORDER_FIELDS[number];
export type OrderErrors = Partial<Record<OrderField, string>>;

export const isValidEmail = (email?: string) =>
  !!email &&
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim()) &&
  email.trim().length <= 50;

interface Options {
  /** Payment method; null while the customer has not chosen one. */
  method: PaymentMethod | null;
  /** The payment section (method choice and terms) is shown, i.e. card payment is not "off". */
  paymentSection: boolean;
  termsAccepted: boolean;
}

export const validateOrder = (
  cart: ICart,
  { method, paymentSection, termsAccepted }: Options
): OrderErrors => {
  const errors: OrderErrors = {};
  const len = (s?: string) => (s ?? "").trim().length;

  if (len(cart.name) <= 3) errors.name = "Unesite ime i prezime.";
  if (len(cart.address) <= 5) errors.address = "Unesite adresu (ulica i broj).";
  if (len(cart.zip) <= 3) errors.zip = "Unesite poštanski broj.";
  if (len(cart.place) <= 1) errors.place = "Unesite mesto.";
  if (len(cart.phone) <= 6) errors.phone = "Unesite broj telefona.";

  // Card payment sends the payment confirmation by email, so it is required then.
  // Otherwise it is optional, but a typo would lose the order confirmation.
  if (method === "CARD" && !isValidEmail(cart.email)) {
    errors.email = "Unesite ispravnu email adresu, na nju šaljemo potvrdu o plaćanju.";
  } else if (len(cart.email) > 0 && !isValidEmail(cart.email)) {
    errors.email = "Email adresa nije ispravna.";
  }

  if (paymentSection && method === null) {
    errors.paymentMethod = "Izaberite način plaćanja.";
  }
  if (paymentSection && !termsAccepted) {
    errors.terms = "Potrebno je da prihvatite uslove kupovine i plaćanja.";
  }
  return errors;
};

export const firstInvalidField = (errors: OrderErrors): OrderField | undefined =>
  ORDER_FIELDS.find((f) => errors[f]);

/** DOM id of the element wrapping a field, used to scroll to it. */
export const orderFieldId = (field: OrderField) => `order-field-${field}`;

/** Scrolls the field into view and puts the cursor in it, if it is a text input. */
export const focusOrderField = (field: OrderField) => {
  const el = document.getElementById(orderFieldId(field));
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  const input = el.querySelector("ion-input, ion-textarea") as
    | HTMLIonInputElement
    | null;
  // Focus after the scroll has started, or the browser jumps instead of scrolling smoothly.
  if (input) setTimeout(() => input.setFocus(), 300);
};
